import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, LoaderCircle } from 'lucide-react';
import { debugAuthState } from '@/utils/auth';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const Login: React.FC = () => {
  const { login, isAuthenticated, isAdmin, isLoading, session } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  
  useAuthRedirect(true);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    setIsSubmitting(true);
    setAuthError(null);
    
    const loginTimeoutId = setTimeout(() => {
      setIsSubmitting(false);
      setAuthError("Login request timed out. Please try again.");
    }, 10000);
    
    try {
      const loginSuccess = await login(values.email, values.password);
      
      if (loginSuccess) {
        clearTimeout(loginTimeoutId);
        
        // Get the original path from sessionStorage (or default to dashboard)
        const redirectPath = sessionStorage.getItem('auth_redirect_path');
        const destination = redirectPath || (isAdmin ? '/admin/dashboard' : '/dashboard');
        
        // Clear the saved path
        sessionStorage.removeItem('auth_redirect_path');
        
        // Log that we're about to navigate
        console.log(`Login successful, redirecting to: ${destination}`);
        
        // Explicitly navigate to the destination
        navigate(destination, { replace: true });
        
      } else {
        console.error("Login failed without throwing an error");
        setAuthError("Login failed. Please check your credentials and try again.");
      }
    } catch (error: any) {
      console.error("Login error caught in Login component:", error);
      
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        
        const errorMessage = error.response.data?.message || 
                            error.response.data?.error ||
                            error.message ||
                            'Login failed. Please check your credentials and try again.';
        
        setAuthError(errorMessage);
      } else {
        setAuthError(error.message || 'Login failed. Please check your credentials and try again.');
      }
    } finally {
      clearTimeout(loginTimeoutId);
      setIsSubmitting(false);
    }
  };

  const handleRequestInvitation = () => {
    toast.info('Please contact your administrator to request an invitation.');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 relative">
      <div className="absolute inset-0 z-[-1] bg-[radial-gradient(circle_at_top_right,theme(colors.primary.DEFAULT/10%),transparent_40%)]"></div>
      
      <div className="absolute top-4 left-4 z-10">
        <Button variant="ghost" size="sm" asChild className="gap-1">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </Button>
      </div>
      
      <Card className="mx-auto w-full max-w-md animate-scale-in border-gray-8 relative z-10">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center">
            <Link to="/" className="mb-4 inline-flex items-center space-x-2">
              <Book className="h-6 w-6 text-yellow" />
              <span className="text-xl font-semibold text-yellow">C TWO</span>
            </Link>
          </div>
          <CardTitle className="text-2xl">Welcome back</CardTitle>
          <CardDescription>
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {authError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{authError}</AlertDescription>
            </Alert>
          )}
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="email" 
                        placeholder="your.email@example.com" 
                        className="bg-gray-9 border-gray-7"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          {...field} 
                          type={showPassword ? "text" : "password"} 
                          placeholder="••••••••" 
                          className="bg-gray-9 border-gray-7"
                          disabled={isSubmitting}
                        />
                        <button 
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-5 hover:text-gray-3"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isSubmitting}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full bg-yellow text-black hover:bg-yellow-light"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : 'Sign in'}
              </Button>
              
              <div className="mt-2 text-right text-sm">
                <Link to="/forgot-password" className="text-yellow hover:underline">
                  Forgot password?
                </Link>
              </div>
              
              <div className="relative my-6">
                <Separator className="bg-gray-7" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-gray-4">
                  NO ACCOUNT?
                </span>
              </div>
              
              <Button 
                type="button" 
                variant="outline" 
                className="w-full border-gray-7 hover:bg-gray-8"
                onClick={handleRequestInvitation}
                disabled={isSubmitting}
              >
                Request an invitation
              </Button>
              
              <div className="mt-4 text-center text-sm text-muted-foreground">
                <p>Have an invitation?{' '}
                  <Link to="/invitation" className="text-yellow hover:underline">
                    Sign up here
                  </Link>
                </p>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
